/**
 * A FRESH CODE, asked for inside a sheet where the request door stands behind one: adding a person,
 * or changing how the household pays (CLAUDE.md binding rule 5, `docs/identity.md` section 4). The
 * door answers `403 step_up_required` and writes nothing; this asks for a code, takes the one she
 * types, and hands back to the sheet, which sends the same ask again with the same key. It is the
 * sign-in screen's code step (`screens/SignIn.tsx`), drawn in a sheet, with its words where they fit
 * a person already signed in (`FRESH_CODE`).
 *
 * - TO THE ADDRESS OR NUMBER SHE SIGNED IN WITH, and nothing typed: the Worker stamps the same
 *   session when the code is typed (`api/src/routes/auth.ts`), so the proof is hers and nobody
 *   else's. Signed in by her mobile number (home #11), the code is asked for with that number, as
 *   /api/me names it, and the Worker texts it through the same sender a sign-in by number uses; the
 *   step then says "text" and names the number where it says "email" and names the address. Only a
 *   host that can text makes a session by number, but a host can stop texting while she is signed
 *   in, so for a session by number the step first asks /api/health, as the sign-in screen does, and
 *   asks for a code only on a clear yes (`signInByText`). Short of one, no code could reach her, and
 *   the step asks for none and never says one is on its way: it says why, and that it cannot be
 *   done here, with the phone, as it said before home #11 to a person with no address to email a
 *   code to, and offers no field. While /api/health is out it draws nothing of its own. A session
 *   by email never asks it, and its step is as it was.
 * - IT NEVER SAYS A CODE WAS SENT (binding rule 3): the Worker answers the same whether or not the
 *   address is on file, so the step says one may be on its way, as sign-in does. A step-up never
 *   meets the ceiling across every sign-in, but an ask whose session has ended since is a fresh one
 *   and can, and then the step says sign-in is busy, as sign-in does, and keeps what she typed. Past
 *   thirty codes in an hour for her address or number (`429 too_many_codes`, the owner's word of
 *   2026-10-20) it says so, with the phone, as sign-in does, and keeps what she typed.
 * - AND NEVER "ON ITS WAY" FOR A CODE THAT IS NOT (the owner's word of 2026-10-20: "User messages
 *   always gives info, never silent"). The line under why says "on its way" only once the Worker
 *   took the ask, and nothing while the ask on arrival is out. An ask refused, on arrival or by "Send
 *   a new code", past the flood guard or the ceiling, with sign-in closed, unread or failed, says
 *   that answer's own line there instead, with the phone. Where the Worker named the ask, the step
 *   asks what came of its send for a little more than thirty seconds (`lib/codeSent.ts`), and a
 *   code Microsoft or the phone system did not take is said there too, with why, in place of "on
 *   its way" and of the line that said a new one is; a fresh code goes only where she signed in
 *   with, so it offers no other way in. That line is in a live region drawn afresh each time, so
 *   each is heard. A code typed while the guess lock holds (`429 codes_locked`) says codes are
 *   paused, and until when.
 * - ONE CODE ASKED FOR ON ARRIVAL, however often the step is drawn, and another only when she asks.
 *   The address and the code travel only in the body of a POST (`api.ts`).
 * - NONE ASKED FOR WITHIN TEN MINUTES OF THE LAST (round four, slice M3, `docs/seam.md` P59). Only
 *   the newest code works (`api/src/db/codes.ts`), so a step drawn again within minutes, after "I
 *   can't pay by bank", a sheet closed and opened, or a reload while she was in her mail, would stop
 *   the code she may already have open from working. The tab keeps when it last asked for a code,
 *   and the id the Worker named that ask by (`lib/fresh.ts`), and a step drawn within ten minutes of
 *   it asks for none. It first asks what came of that ask's send, at once, since it may have failed
 *   after the step that asked had gone, and says nothing until that answers: then that one is
 *   already on its way (`codeAlreadyOnItsWay`), or why it was not sent (the review of 303dfc2). A
 *   send that failed is forgotten by the tab, so the next step asks for a code afresh. "Send a new
 *   code" stays hers, and once she has asked for one the step says a code is on its way, as on any
 *   arrival. A code that works is spent, so the tab forgets the ask, and knows a fresh code holds
 *   for fifteen minutes (`codeWorked`); a door that asks for a code again says none stands
 *   (`noFreshCode`).
 * - THE CURSOR GOES TO THE FIELD on arrival and after every answer, and the line that answers a
 *   press is in a live region that is always drawn, drawn afresh each time, so the same line said
 *   twice is heard twice. One request at a time: a press while one is out does nothing.
 * - ONCE THE CODE WORKS, the sheet does what it asked for (`onProved`), and says what it returns if
 *   it stays open: a failure, or that it cannot be done here. The Worker takes a code once, and the
 *   session it stamped stays stepped up for fifteen minutes, so trying again does what the sheet
 *   asked for again without typing the code a second time; only once the door asks for a code again
 *   (`step_up`) does the step say that one has run out, and take the next she types.
 * - WHERE WHAT DRAWS IT COVERS THE SCREEN while a press is out (`onWaiting`), as the bank and the
 *   card do with the panel while she is taken to Stripe's page (`StripeWay.tsx`, slice V3), it is
 *   told the moment a press goes, before the code is checked, so the cover comes at once and the
 *   submit is never the only thing that says a press is out; and told again where the code itself
 *   ends the press, not accepted, closed or not gone through, which the step then says with the
 *   cursor in its field, once the cover has gone. Once the code works, what follows is what draws
 *   it to say, the cover's end and the focus with it, but for a code that has run out.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import {
  askForCode,
  enterCode,
  getHealth,
  type CodeAsked,
  type CodesLocked,
  type SendFailure,
  type SignedInWith,
  type TooMany,
} from '../api.ts';
import { watchCodeSent, type WatchFrom } from '../lib/codeSent.ts';
import {
  codeAskedLately,
  codeWorked,
  forgetCodeAsked,
  noFreshCode,
  noteCodeAsked,
} from '../lib/fresh.ts';
import { signedInShown } from '../lib/identifier.ts';
import {
  codeAlreadyOnItsWay,
  codeAlreadyOnItsWayByText,
  codeNotSentBeforePhone,
  codeOnItsWay,
  codeOnItsWayByText,
  codesPausedBeforePhone,
  FAILED_BEFORE_PHONE,
  FRESH_CODE,
  freshWhyByText,
  SIGN_IN_BUSY_BEFORE_PHONE,
  TOO_MANY_CODES_BEFORE_PHONE,
  tooManyBeforePhone,
} from '../words.ts';
import { NAVY_BUTTON, TEXT_LINK } from './buttons.ts';
import { Phone } from './Phone.tsx';

/**
 * What the step says after a press: a code that did not work, a new code, a failure, closed, a code
 * that worked and has run out, what the sheet asked for refused past the request door's own limits,
 * a code asked for past the ceiling across every sign-in, or past thirty codes in an hour for her
 * address or number (`too_many_codes`, the owner's word of 2026-10-20), either of which asks
 * nothing, or a code typed while the guess lock holds (`locked`, the same day's word, never silent).
 */
export type FreshSaid =
  | 'wrong'
  | 'new_code'
  | 'failed'
  | 'closed'
  | 'run_out'
  | 'too_many'
  | 'busy'
  | 'too_many_codes'
  | 'locked';

/**
 * Where the last code asked for stands, said under why (the owner's word of 2026-10-20, never
 * silent): its ask still out, or what came of the send of the one the tab asked lately, which says
 * nothing; on its way, or already on its way where the tab asked within ten minutes and that send
 * is no failure; refused, with what refused it; or not sent, with why.
 */
type Standing =
  | { readonly kind: 'asking' }
  | { readonly kind: 'on_its_way' }
  | { readonly kind: 'already' }
  | { readonly kind: 'refused'; readonly said: FreshSaid }
  | { readonly kind: 'not_sent'; readonly why: SendFailure };

/**
 * What came of what the sheet asked for, once a code worked: a failure, that it cannot be done here,
 * the door asking for a fresh code again, the door's own limits (`429 too_many`) with whose count
 * and when, as the door said them (round six, slice S1), or nothing for the step to say.
 */
export type ProvedCame =
  'failed' | 'closed' | 'step_up' | { readonly tooMany: TooMany | undefined } | null;

/** A code is six digits. Spaces a person types between them are taken out first. */
const SIX_DIGITS = /^\d{6}$/;

/**
 * What the step says of a code asked for that did not come to `asked`. The Worker reads the address
 * or number /api/me named, so a `400` that says it could not is a failure here, as any other.
 */
const saidOf = (answer: Exclude<CodeAsked, 'asked'>): FreshSaid =>
  answer === 'unreadable' ? 'failed' : answer;

/**
 * The step's words that name how the code comes: emailed to her address, or texted to her number
 * where she signed in by it (home #11), as the sign-in screen's two code steps say them.
 */
const NAMED = {
  email: {
    why: (why: string) => why,
    onItsWay: codeOnItsWay,
    alreadyOnItsWay: codeAlreadyOnItsWay,
    newCodeSent: FRESH_CODE.newCodeSent,
  },
  text: {
    why: freshWhyByText,
    onItsWay: codeOnItsWayByText,
    alreadyOnItsWay: codeAlreadyOnItsWayByText,
    newCodeSent: FRESH_CODE.newCodeTexted,
  },
} as const;

/** The sign-in screen's underlined field, at least 48px tall. */
const FIELD =
  'mt-1 block min-h-tap w-full border-b-2 border-border-strong bg-transparent py-2.5 text-body ' +
  'text-text-body';

interface FreshCodeProps {
  /**
   * The address she signed in with, which the code is emailed to, or the mobile number, which it
   * is texted to (home #11).
   */
  readonly signedInWith: SignedInWith;
  /** Why a code is asked for, said first, in its emailed words; texted, the step says it so. */
  readonly why: string;
  /** The submit button's words: what typing the code goes on to do. */
  readonly submit: string;
  /** What is said, before the phone, when no code can be asked for here: sign-in has closed. */
  readonly closedBeforePhone: string;
  /** The code worked: does what the sheet asked for, and gives what to say if the sheet stays. */
  readonly onProved: () => Promise<ProvedCame>;
  /**
   * Where the step stands in what draws it, such as the margin above it: the step brings none of
   * its own, so a screen that spaces its blocks with a gap gives it that gap and no more.
   */
  readonly className?: string;
  /**
   * What draws the step covers the screen while a press is out: called with true the moment a press
   * goes, and with false where the code itself ends it, which the step then says. Once the code
   * works, `onProved` says what follows. Without it the submit alone dims while a press is out.
   */
  readonly onWaiting?: (waiting: boolean) => void;
}

/**
 * A line, and the count of lines said so far, which draws each one as a new node; for the door's own
 * limits, whose count and when; for the guess lock, until when.
 */
interface Line {
  readonly said: FreshSaid;
  readonly count: number;
  readonly limit?: TooMany;
  readonly until?: CodesLocked['locked'];
}

/** Where the last code stands, and the count of standings so far, which draws each one anew. */
interface Stood {
  readonly standing: Standing;
  readonly count: number;
}

/**
 * The words of a refusal with the phone after them: a code asked for or typed that did not get
 * through, closed, past the ceiling or the flood guard, or what the sheet asked for past the door's
 * own limits.
 */
function refusalBeforePhone(
  said: FreshSaid,
  closedBeforePhone: string,
  limit: TooMany | undefined,
  until: CodesLocked['locked'] | undefined,
): string {
  switch (said) {
    case 'closed':
      return closedBeforePhone;
    case 'too_many':
      return tooManyBeforePhone(limit);
    case 'busy':
      return SIGN_IN_BUSY_BEFORE_PHONE;
    case 'too_many_codes':
      return TOO_MANY_CODES_BEFORE_PHONE;
    case 'locked':
      return codesPausedBeforePhone(until ?? null);
    default:
      return FAILED_BEFORE_PHONE;
  }
}

export function FreshCode({
  signedInWith,
  why,
  submit,
  closedBeforePhone,
  onProved,
  className,
  onWaiting,
}: FreshCodeProps) {
  const fieldId = useId();
  const aboutId = useId();
  const [code, setCode] = useState('');
  const [line, setLine] = useState<Line | null>(null);
  const [busy, setBusy] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  /** What the code is asked for and typed with: the address or number, as /api/me named it. */
  const identifier = signedInWith.value;
  const named = NAMED[signedInWith.channel];
  /** Signed in by her mobile number, so the code goes by text, if this host can still send one. */
  const byText = signedInWith.channel === 'text';
  /**
   * For a session by number, whether /api/health says a code can go by text: null until it answers.
   * A session by email never asks, and its code is emailed as it always was.
   */
  const [textable, setTextable] = useState<boolean | null>(null);
  /** The line that says to call, where no code can be texted to her here. */
  const noTexts = useRef<HTMLParagraphElement>(null);
  /** Read synchronously, so a second press in the same moment as the first is refused. */
  const out = useRef(false);
  const mounted = useRef(true);
  const lines = useRef(0);
  /** The code asked for on arrival, once, however often the arrival's effect runs. */
  const asked = useRef(false);
  /**
   * The code the tab asked for within ten minutes of this step's arrival, or null: the step asks
   * for none, until she asks for a new one (`lib/fresh.ts`). Where the Worker named that ask, the
   * step asks what came of its send before it says the code is already on its way.
   */
  const [kept] = useState(() => codeAskedLately());
  /**
   * Where the last code asked for stands, said under why: nothing while the ask on arrival is out,
   * or while what came of the kept ask's send is asked; then on its way, already on its way,
   * refused, or not sent (never silent, the owner's word of 2026-10-20).
   */
  const [stood, setStood] = useState<Stood>(() => ({
    standing: { kind: kept !== null && kept.ask === null ? 'already' : 'asking' },
    count: 0,
  }));
  const standings = useRef(0);
  /** Stops asking what came of the last code's send, while that is being asked. */
  const watching = useRef<(() => void) | null>(null);
  /** A code typed here worked, and the door has not asked for another since. */
  const proved = useRef(false);
  /**
   * Counts the times the cursor is sent back to the field after a press, which it is once what that
   * press changed is drawn: a cover that has gone has then let the field be reached again.
   */
  const [refocused, setRefocused] = useState(0);

  const say = (said: FreshSaid, limit?: TooMany, until?: CodesLocked['locked']) => {
    lines.current += 1;
    setLine({ said, count: lines.current, limit, until });
  };

  /** Where the last code stands now, drawn as a new node so it is heard. */
  const stand = (standing: Standing) => {
    standings.current += 1;
    setStood({ standing, count: standings.current });
  };

  const stopWatching = () => {
    watching.current?.();
    watching.current = null;
  };

  /**
   * The Worker took the ask and named it: what came of its send is asked about for a while
   * (`lib/codeSent.ts`), and a send that failed is said in place of "on its way", the line that said
   * a new code is going with it. Not sent, the ask is forgotten by the tab, so no step drawn after
   * says its code is already on its way, and the next asks for one afresh (the review of 303dfc2).
   * Any earlier asking stops. A Worker that names no ask is asked nothing.
   */
  const watch = (ask: string | null, from?: WatchFrom) => {
    stopWatching();
    if (ask === null) return;
    watching.current = watchCodeSent(
      ask,
      (why) => {
        watching.current = null;
        forgetCodeAsked(ask);
        if (!mounted.current) return;
        stand({ kind: 'not_sent', why });
        setLine((current) => (current?.said === 'new_code' ? null : current));
      },
      from,
    );
  };

  const begin = () => {
    out.current = true;
    setBusy(true);
    setLine(null);
  };

  const end = () => {
    out.current = false;
    setBusy(false);
  };

  /** A press goes: one at a time, and what draws the step covers the screen where it does. */
  const pressGoes = () => {
    begin();
    onWaiting?.(true);
  };

  /** The code itself ended the press: the cover goes, and the step says why. */
  const codeEnded = () => {
    end();
    onWaiting?.(false);
  };

  /** Sends the cursor back to the field, once what the press changed is drawn. */
  const refocus = () => setRefocused((count) => count + 1);

  useEffect(() => {
    if (refocused > 0) field.current?.focus();
  }, [refocused]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopWatching();
    };
  }, []);

  /**
   * The one code asked for on arrival, unless the tab asked for one within the last ten minutes,
   * which would stop working were another asked for now. Only once the Worker took it does the step
   * say it is on its way; refused, it says what refused it instead.
   */
  const askOnArrival = () => {
    if (kept !== null) return;
    out.current = true;
    void askForCode(identifier).then(({ answer, ask }) => {
      out.current = false;
      // Kept whether or not the step is still drawn: the code was asked for all the same.
      if (answer === 'asked') noteCodeAsked(ask);
      if (!mounted.current) return;
      if (answer === 'asked') {
        stand({ kind: 'on_its_way' });
        watch(ask);
      } else {
        stand({ kind: 'refused', said: saidOf(answer) });
      }
    });
  };

  // On arrival: the focus to the field, and one code asked for. For a session by number, /api/health
  // is asked first, and a code only on its clear yes; the focus goes once it has answered.
  useEffect(() => {
    if (!byText) field.current?.focus();
    if (asked.current) return;
    asked.current = true;
    if (!byText) {
      askOnArrival();
      return;
    }
    void getHealth().then((health) => {
      if (!mounted.current) return;
      setTextable(health.signInByText);
      if (health.signInByText) askOnArrival();
    });
  }, []);

  // Once /api/health has answered a session by number: the focus to the field, or to the line that
  // says to call.
  useEffect(() => {
    if (textable === null) return;
    (field.current ?? noTexts.current)?.focus();
  }, [textable]);

  // The code asked lately, where the Worker named its ask, once the step draws its field (for a
  // session by number, on /api/health's clear yes): what came of its send is asked at once, since
  // it may have failed after the step that asked for it had gone, and the step says "already on its
  // way" only once that is no failure, and why it was not sent where it failed (the review of
  // 303dfc2). Asked again should the step be drawn again, as React's checks in development draw
  // it twice; the asking before stops first.
  useEffect(() => {
    if (kept === null || kept.ask === null) return;
    if (byText && textable !== true) return;
    watch(kept.ask, { atOnce: true, heard: () => stand({ kind: 'already' }) });
  }, [textable]);

  async function sendAgain() {
    if (out.current) return;
    begin();
    const { answer, ask } = await askForCode(identifier);
    if (answer === 'asked') noteCodeAsked(ask);
    if (!mounted.current) return;
    end();
    if (answer === 'asked') {
      // She asked for another, so the next she types is the one taken, and it is on its way.
      proved.current = false;
      stand({ kind: 'on_its_way' });
      say('new_code');
      setCode('');
      watch(ask);
    } else {
      // Nothing was asked for, so what she typed stays, and a code that worked still counts; what
      // refused it is said where "on its way" was (never silent, the owner's word of 2026-10-20).
      stand({ kind: 'refused', said: saidOf(answer) });
    }
    field.current?.focus();
  }

  /**
   * Does what the sheet asked for, the code having worked, and says what came of it. Where what
   * draws the step covers the screen, what follows is its to say, and the cursor stays where it put
   * it, unless the step has something to say.
   */
  async function proceed() {
    const then = await onProved();
    // The door asked for a code again: none stands, whether or not the step is still drawn.
    if (then === 'step_up') noFreshCode();
    if (!mounted.current) return;
    end();
    if (then === 'step_up') {
      proved.current = false;
      setCode('');
      say('run_out');
    } else if (then !== null && typeof then === 'object') {
      say('too_many', then.tooMany);
    } else if (then !== null) {
      say(then);
    }
    if (then !== null || onWaiting === undefined) refocus();
  }

  async function enter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (out.current) return;
    if (proved.current) {
      pressGoes();
      await proceed();
      return;
    }
    const typed = code.replace(/\s/g, '');
    if (!SIX_DIGITS.test(typed)) {
      say('wrong');
      field.current?.focus();
      return;
    }
    pressGoes();
    const answer = await enterCode(identifier, typed);
    // The Worker stamped the session as it took the code: a fresh code holds, and this one is spent.
    if (answer === 'signed_in') codeWorked();
    if (!mounted.current) return;
    if (answer === 'signed_in') {
      proved.current = true;
      await proceed();
      return;
    }
    codeEnded();
    if (typeof answer === 'object') {
      // The guess lock refused the try before it was compared: it says so, and until when, and
      // what she typed stays for once it lifts (never silent, the owner's word of 2026-10-20).
      say('locked', undefined, answer.locked);
    } else if (answer === 'not_accepted') {
      say('wrong');
      setCode('');
    } else {
      say(answer === 'closed' ? 'closed' : 'failed');
    }
    refocus();
  }

  /** The address, or the number as she reads one, that the step names. */
  const shown = signedInShown(signedInWith);

  /** What the line under why says of the last code asked for, or null for nothing yet. */
  const standingWords = (standing: Standing) => {
    switch (standing.kind) {
      case 'asking':
        return null;
      case 'on_its_way':
        return named.onItsWay(shown);
      case 'already':
        return named.alreadyOnItsWay(shown);
      case 'refused':
        return (
          <>
            {refusalBeforePhone(standing.said, closedBeforePhone, undefined, undefined)}{' '}
            <Phone after="." />
          </>
        );
      case 'not_sent':
        return (
          <>
            {codeNotSentBeforePhone(standing.why, signedInWith.channel, false)} <Phone after="." />
          </>
        );
    }
  };

  // Signed in by her number, and /api/health has not yet said whether a code can be texted here:
  // nothing of the step's own is drawn, so nothing says a code is on its way before it can be.
  if (byText && textable === null) return <div className={className} />;

  // No code can be texted to her here: why, and that it cannot be done here, with the phone.
  if (byText && textable === false) {
    return (
      <div className={className}>
        <p className="text-body text-pretty text-text-body">{why}</p>
        <p ref={noTexts} tabIndex={-1} className="mt-3 text-body text-pretty text-text-body">
          {closedBeforePhone} <Phone after="." />
        </p>
      </div>
    );
  }

  return (
    <form
      method="post"
      onSubmit={(event) => void enter(event)}
      className={`flex flex-col${className === undefined ? '' : ` ${className}`}`}
    >
      <div id={aboutId}>
        <p className="text-body text-pretty text-text-body">{named.why(why)}</p>
        {/* Where the last code stands, in a live region drawn afresh each time, so a refusal or a
            send that failed is heard as it takes the place of "on its way". */}
        <div aria-live="polite">
          {stood.standing.kind === 'asking' ? null : (
            <p
              key={stood.count}
              className="mt-2 text-body text-pretty wrap-break-word text-text-body"
            >
              {standingWords(stood.standing)}
            </p>
          )}
        </div>
      </div>

      <label htmlFor={fieldId} className="mt-5 block text-body font-semibold text-text-body">
        {FRESH_CODE.code}
      </label>
      <input
        ref={field}
        id={fieldId}
        type="text"
        name="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(event) => setCode(event.target.value)}
        aria-describedby={aboutId}
        className={FIELD}
      />

      <div aria-live="polite">
        {line === null ? null : (
          <p key={line.count} className="mt-3 text-body text-pretty text-text-body">
            {line.said === 'wrong' ? (
              FRESH_CODE.wrong
            ) : line.said === 'run_out' ? (
              FRESH_CODE.runOut
            ) : line.said === 'new_code' ? (
              named.newCodeSent
            ) : (
              <>
                {refusalBeforePhone(line.said, closedBeforePhone, line.limit, line.until)}{' '}
                <Phone after="." />
              </>
            )}
          </p>
        )}
      </div>

      <button
        type="button"
        aria-disabled={busy ? true : undefined}
        onClick={() => void sendAgain()}
        className={`mt-3 self-start text-body ${TEXT_LINK} cursor-pointer aria-disabled:cursor-wait`}
      >
        {FRESH_CODE.newCode}
      </button>

      <button
        type="submit"
        aria-disabled={busy ? true : undefined}
        className={`mt-6 cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:opacity-60 ${NAVY_BUTTON}`}
      >
        {submit}
      </button>
    </form>
  );
}
